import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform } from "react-native"

import { isIos } from "@braintree/browser-detection"
import styled from "styled-components/native"

import { ShorthandDuration } from "@treefort/lib/parse-duration"

import { getTextStyleObject, getTextStyleString } from "../lib/text-style"
import { Button } from "./button"
import Column from "./column"
import Modal from "./modal"
import Text from "./text"
import TextInput from "./text-input"
import { useTokens } from "./tokens-provider"

const INPUT_PLACEHOLDER = "0"

const StyledTextInput = styled(TextInput)`
  ${(props) => getTextStyleString("headingLarge", props.theme)};
  padding: ${({ theme }) => theme.textInput.paddingHorizontal}px;
  height: ${(props) =>
    getTextStyleObject("headingLarge", props.theme).lineHeight +
    props.theme.textInput.paddingHorizontal * 2}px;
  color: ${(props) => props.theme.colors.text.primary};
  /* Unset the default min-width applied by most browsers */
  min-width: 0;
  max-width: 100px;
  text-align: center;
  /* Hide the focus ring. A big a11y faux pas. The input is giant though, it's
  the only one on the page, the default focus ring looks bad, and RN web makes
  it very hard to change the styles. */
  ${Platform.OS === "web" ? "outline-width: 0;" : ""}
  ${Platform.OS === "web" ? "outline-style: none;" : ""}
`

export function DurationInputModal({
  label,
  open,
  valueMinutes,
  onChange,
  onClose,
}: {
  label: string
  open: boolean
  valueMinutes?: number
  onChange: (value: ShorthandDuration) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [minutes, setMinutes] = useState(valueMinutes)
  const { tokens } = useTokens()

  const inputText = useRef<TextInput>(null)

  const submit = () => {
    if (minutes) {
      onChange(`${minutes}m`)
    }
    onClose()
  }

  // Mobile Safari is just awful about auto-focusing and selecting text input.
  const isMobileSafari = Platform.OS === "web" && isIos()

  const value = minutes?.toString() || ""
  return (
    <Modal
      title={label}
      open={open}
      onPressCloseButton={onClose}
      onPressOutside={onClose}
      backgroundColor="tertiary"
      portalHost="foreground"
      maxWidth={250}
      avoidKeyboard
    >
      {() => (
        <Column alignItems="center" padding="large" gap="large">
          <Column alignItems="center">
            <StyledTextInput
              ref={inputText}
              value={value}
              autoFocus={!isMobileSafari}
              selectTextOnFocus={!isMobileSafari}
              onFocus={(event) => {
                if (isMobileSafari) {
                  const input = event.target as unknown as HTMLInputElement
                  setTimeout(() =>
                    input.setSelectionRange(
                      input.value.length,
                      input.value.length,
                    ),
                  )
                }

                if (Platform.OS === "web") {
                  // On the web, selectTextOnFocus={true} works to select the contents on focus,
                  // and for some reason the web TextInput ref doesn't have a setSelection method.
                  return
                }

                // On native, selectTextOnFocus doesn't select the text, so do it ourselves.
                inputText.current?.setSelection(0, value.length)
              }}
              onChangeText={(value) => {
                const mins = parseInt(value)
                setMinutes(isNaN(mins) ? undefined : mins)
              }}
              maxLength={3}
              keyboardType="numeric"
              onSubmitEditing={submit}
              placeholder={INPUT_PLACEHOLDER}
              placeholderTextColor={tokens.textInput.placeholderTextColor}
            />
            <Text textStyle="body">{t("minutes")}</Text>
          </Column>
          <Button
            id="duration-input-modal-save"
            type="primary"
            onPress={submit}
          >
            {t("Save")}
          </Button>
        </Column>
      )}
    </Modal>
  )
}
